import React from "react";
import PageHeader from "../../../components/layout/PageHeader";

/** One selling point: what the thing does, and what that gets you. */
export interface Benefit {
  /** The design's own glyph, imported as a URL. Each file draws its own pale
   *  teal disc, so nothing here puts a background behind it. */
  icon: string;
  title: string;
  detail: string;
}

interface ComingSoonPitchProps {
  /** What the screen is called, in the title row and the browser tab. */
  title: string;
  /** The headline of the pitch — what the unbuilt thing is. */
  heading: React.ReactNode;
  /** The line under it. A node, because one of these emphasises part of it. */
  subheading: React.ReactNode;
  benefits: Benefit[];
  /**
   * The way in, under the benefits — where there is one to offer. Left off on
   * a screen with nothing to press yet, rather than drawn as a dead button.
   */
  action?: React.ReactNode;
  /**
   * The picture beside the copy: the whole right-hand cell of the grid, wrapper
   * and all. Each screen frames its own — one centres a phone inside the card,
   * the other runs a browser window off the edge of it — and a prop that took
   * only the image would have to grow a flag per framing.
   */
  artwork: React.ReactNode;
}

/**
 * The shape both unbuilt-product screens share: a pitch on the left, a picture
 * of the thing on the right.
 *
 * A component rather than two pages that happen to look alike. They are the
 * same promise made about two products — the client app and the portfolio page
 * — and the day the pill stops saying "coming soon", or the benefit rows grow a
 * hover, it should happen to both without anybody remembering the second one.
 *
 * Deliberately not the stand-in `PlaceholderPage` every other unbuilt nav item
 * opens. That one answers when; these two answer what, because both are things
 * a nutritionist would hand to their own clients, and somebody who presses the
 * nav item is asking what they will be handing over.
 */
const ComingSoonPitch: React.FC<ComingSoonPitchProps> = ({
  title,
  heading,
  subheading,
  benefits,
  action,
  artwork,
}) => (
  <div className="p-page pt-page-top">
    <PageHeader title={title} />

    {/* One height, stated once, for both screens — which is what puts the pill
        in the same place on each. The number is the portfolio mock's own: that
        file is 699x601, so it draws 602 tall in its 700px column and 739 in its
        860px one, and the card is that plus the ~30px the artwork is inset from
        the top. Sized to the picture rather than to the window because a card
        taller than the mock leaves a band of empty white the artwork cannot
        fill without being blown up — scaling it to reach the bottom border is
        what pushed the drawing off its own scale.

        `overflow-hidden` is load-bearing, not tidiness: it is what crops the
        artwork that runs past the card's edge. */}
    <div className="mt-4 3xl:mt-6 overflow-hidden card-surface border border-line bg-white lg:h-[632px] 3xl:h-[770px]">
      {/* One column until `lg`, where the two finally fit side by side. An even
          split rather than letting the artwork take its own width: the drawings
          are wide, and a track sized to them leaves the pitch in a gutter and
          wraps the headline over four lines. Half the card each means the copy
          gets the same width on both screens, and the picture is cropped by the
          card's edge instead of the copy paying for it.

          `items-stretch` so each artwork can centre itself down the full height
          of a card that no longer ends where its content does. */}
      <div className="grid h-full items-stretch gap-10 lg:grid-cols-2 lg:gap-6">
        {/* Set down the card by a stated amount rather than centred in it. The
            two screens' copy is not the same height — one ends in a button, and
            their subheadings wrap differently — so centring lands the pill tens
            of pixels apart between them. A fixed inset is the same on both, and
            these two are about where centring would put the copy anyway. */}
        <div className="px-6 py-10 sm:px-10 sm:py-14 lg:self-start lg:pl-14 3xl:pl-20 lg:pr-0 lg:pt-20 3xl:pt-32">
          {/* The purple the app already reserves for a state that has not
              happened yet — a scheduled call, an unstarted plan. */}
          <span className="inline-flex items-center rounded-full bg-[#F3EEFF] px-3 py-1 text-[12px] 3xl:text-[13px] font-medium text-[#8A5FED] border-[0.5px] border-[#AA91E4]">
            Coming soon
          </span>

          <h2 className="mt-4 text-[24px] sm:text-[28px] lg:text-[30px] 3xl:text-[34px] font-semibold leading-tight text-[#00424B]">
            {heading}
          </h2>
          <p className="mt-3 max-w-lg text-[14px] 3xl:text-[16px] leading-relaxed text-ink-muted">
            {subheading}
          </p>

          <ul className="mt-8 3xl:mt-10 space-y-5">
            {benefits.map(({ icon, title: name, detail }) => (
              <li key={name} className="flex items-start gap-3.5">
                {/* Empty alt: the glyph repeats the heading beside it, so a
                    screen reader that read both would say each benefit twice. */}
                <img
                  src={icon}
                  alt=""
                  className="mt-0.5 h-[33px] w-[33px] 3xl:h-10 3xl:w-10 flex-shrink-0"
                />
                <span className="min-w-0">
                  <span className="block text-[14px] 3xl:text-[16px] font-semibold text-ink">
                    {name}
                  </span>
                  <span className="mt-0.5 block text-[12px] 3xl:text-[14px] text-ink-muted">
                    {detail}
                  </span>
                </span>
              </li>
            ))}
          </ul>

          {/* Text, not a link. There is more to say about these and nowhere yet
              to say it, and a teal line that goes nowhere when pressed is a
              worse promise than one that never offered. It becomes a link the
              day there is a page behind it. */}
          {/* Close to the list it finishes, and well clear of the action under
              it: this line is the tail of the benefits, not a third thing
              between them and the button. */}
          <p className="mt-4 3xl:mt-5 text-[14px] 3xl:text-[16px] font-medium text-brand">
            And many more benefits
          </p>

          {action && <div className="mt-10 3xl:mt-12">{action}</div>}
        </div>

        {artwork}
      </div>
    </div>
  </div>
);

export default ComingSoonPitch;
